import React, { useEffect, useState } from "react";
import { RotatingLines } from "react-loader-spinner";
import {
  X,
  Building2,
  Calendar,
  CreditCard,
  DollarSign,
  FileText,
} from "lucide-react";
import { useAuth } from "../../context/Auth";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

const NewExpense = ({ modalOpen, setModalOpen }) => {
  const { requestWithAuth } = useAuth();
  const [submittingForm, setSubmittingForm] = useState(false);
  const [data, setData] = useState({});
  const [branches, setBranches] = useState([]);

  const handleFormChange = (e) => {
    setData({
      ...data,
      [e.target.name]: e.target.value,
    });
  };

  const fetchBranches = async () => {
    try {
      const data = await requestWithAuth(
        `/company/branches/?pageSize=100`,
        "GET"
      );
      setBranches(data?.results);
    } catch (error) {
      console.error("Error fetching users data:", error);
    }
  };

  const recordExpense = async (e) => {
    e.preventDefault();

    Swal.fire({
      icon: "question",
      title: "Are you sure you want to proceed?",
      showDenyButton: true,
      confirmButtonText: "Yes Proceed!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setSubmittingForm(true);
          const body = JSON.stringify(data);

          const response = await requestWithAuth(
            `/company/expense/`,
            "POST",
            body,
            {
              "Content-Type": "application/json",
            }
          );
          Swal.fire({
            icon: "success",
            title: "Success!",
            text: "Expense Recorded Successfully",
          }).then(() => {
            window.location.reload();
          });
        } catch (error) {

          toast.error("Something went wrong!");
        } finally {
          setSubmittingForm(false);
        }
      } else if (result.isDenied) {
        Swal.fire("Changes are not saved", "", "info");
      }
    });
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  if (!modalOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={() => setModalOpen(false)}
      ></div>

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-500 to-blue-600">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg">
              <DollarSign className="text-white" size={24} />
            </div>
            <h2 className="text-xl font-bold text-white">New Expense</h2>
          </div>
          <button
            onClick={() => setModalOpen(false)}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
          >
            <X className="text-white" size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Branch */}
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                  <Building2 size={16} className="text-blue-500" />
                  Branch
                  <span className="text-red-500">*</span>
                </label>
                <select
                  name="branch"
                  onChange={(e) => handleFormChange(e)}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-700"
                >
                  <option value="">Select Branch</option>
                  {branches.map((branch, index) => (
                    <option key={index} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Expense Date */}
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                  <Calendar size={16} className="text-blue-500" />
                  Expense Date
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="date"
                  required
                  onChange={(e) => handleFormChange(e)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
                />
              </div>

              {/* Mode of Payment */}
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                  <CreditCard size={16} className="text-blue-500" />
                  Mode of Payment
                  <span className="text-red-500">*</span>
                </label>
                <select
                  name="payment_mode"
                  onChange={(e) => handleFormChange(e)}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-700"
                >
                  <option value="">Select Mode of Payment</option>
                  <option value="Cash">Cash</option>
                  <option value="Cheque">Cheque</option>
                  <option value="Mobile Money">Mobile Money</option>
                </select>
              </div>

              {/* Expense Amount */}
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                  <DollarSign size={16} className="text-blue-500" />
                  Expense Amount
                  <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium">
                    GHS
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    required
                    min="0"
                    name="amount"
                    onChange={(e) => handleFormChange(e)}
                    placeholder="0.00"
                    className="w-full border border-gray-300 rounded-lg pl-16 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
                  />
                </div>
              </div>

              {/* Expense Narration */}
              <div className="md:col-span-2">
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                  <FileText size={16} className="text-blue-500" />
                  Expense Narration
                  <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="expense_narration"
                  required
                  rows="3"
                  onChange={(e) => handleFormChange(e)}
                  placeholder="Describe the expense..."
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 resize-none"
                />
              </div>
            </div>

            {/* Footer Actions */}
            <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              {submittingForm ? (
                <div className="bg-blue-400 flex items-center gap-2 rounded-lg px-6 py-2.5 text-white">
                  <RotatingLines strokeColor="white" height="16" width="16" />
                  <span className="text-sm font-medium">Submitting...</span>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={recordExpense}
                  className="bg-blue-500 hover:bg-blue-600 rounded-lg px-6 py-2.5 text-white text-sm font-medium transition-colors shadow-sm"
                >
                  Submit Expense
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewExpense;
